/**
 * Module: screens/creator/StatusScreen
 * Role: Step 8/8 — post-submit status polling (FTR-1106 + FTR-1131 chrome
 *       rewrite). Polls GET /verify_status every 30s:
 *         pending → spinner + «1–2 минуты + до 1–2 дней» copy
 *         approved → success + redirect to /tasks in 3s
 *         rejected → rejection_reason + retry button
 *         429 rate-limit (carried from wizard store) → wait-time + retry
 *       Back-pill on every non-approved branch closes the wizard to /profile
 *       (founder report 2026-06-07: any wizard surface must be escapable).
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - @tanstack/react-query:useQuery
 *   - api/creator:getVerifyStatus
 *   - screens/creator/store:useWizardStore
 *   - screens/creator/_chrome/WizardShell
 *   - styles/tokens:P
 *   - components/atoms/Ic
 *
 * Used by: App.tsx route /creator-status
 * Glossary: ai/glossary/buyer.md
 *
 * ADR-023: no window.Telegram / platform literals
 * ADR-022 §7: no openExternal(BOT_LINK)
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getVerifyStatus, recheckOwnership } from '../../api/creator';
import { useWizardStore } from './store';
import { P, T } from '../../styles/tokens';
import { Ic } from '../../components/atoms/Ic';
import {
  WizardBody, WizardCtaBar, WizardFrame, WizardHeader, WizardStepStrip,
} from './_chrome/WizardShell';
import { rejectionReasonLabel } from './_labels';

const POLL_INTERVAL_MS = 30_000;

// BUG-1451 B1: статусы, которые действительно означают «работа идёт» — только они
// получают спиннер с обещанием «1–2 минуты». `needs_info` (ручная очередь оператора)
// и `banned` (терминальный) имеют собственные экраны ниже; всё незнакомое получает
// нейтральный экран без обещаний, а не вечное вращение.
// BUG-1595: `no_application` — это отсутствие заявки, а не работа; у него своя ветка
// с повторной подачей, поэтому в спиннере его больше нет.
// SYMMETRIC — sync with CreatorApplicationStatusEnum (miniapp/src/api/creator.ts)
// и CHECK-констрейнтом creator_applications.status.
const SPINNER_STATUSES = ['pending', 'pending_scraping'];

// TECH-1456: отвал распознавателя — наша проблема, а не ошибка человека.
// Ни причины, ни просьбы переделать, ни обещания «1–2 минуты».
// SYMMETRIC LIMIT (ADR-022) — те же слова живут в двух других рантаймах:
//   - src/domains/buyer/locales/ru.yaml:proofs.messages.pending_review (бот)
//   - src/domains/flow_engine/checkpoint_runner.py:_MANUAL_REVIEW_NOTICE (флоу)
// Гейт: tests/unit/domains/flow_engine/test_checkpoint_escalation_copy.py
const MANUAL_REVIEW_NOTICE =
  'Обычно занимает от нескольких минут до нескольких часов — мы напишем, как только проверим.';

function formatRetryAfter(retryAtMs: number): string {
  const diff = retryAtMs - Date.now();
  if (diff <= 0) return 'пару секунд';
  const hours = Math.ceil(diff / (1000 * 60 * 60));
  if (hours >= 1) return `${hours} ${hours === 1 ? 'час' : hours < 5 ? 'часа' : 'часов'}`;
  const mins = Math.ceil(diff / (1000 * 60));
  return `${mins} ${mins === 1 ? 'минуту' : mins < 5 ? 'минуты' : 'минут'}`;
}

const CENTER: CSSProperties = {
  alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 16, padding: '24px 32px',
};

function Frame({ title, onBack, children }: {
  title: string; onBack: () => void; children: ReactNode;
}) {
  return (
    <WizardFrame>
      <WizardHeader title={title} onBack={onBack} />
      <WizardStepStrip current={8} total={8} />
      {children}
    </WizardFrame>
  );
}

export default function StatusScreen() {
  const navigate = useNavigate();
  const { draft, resetDraft } = useWizardStore();

  const isRateLimited = draft.rateLimitRetryAt !== null;
  const retryAfterMs = draft.rateLimitRetryAt;
  const setRateLimit = useWizardStore((s) => s.setRateLimit);
  // BUG-1595: момент, когда истёк cooldown. Ответ, полученный раньше него, —
  // это ещё stale cache: до свежего verify_status подачу не разрешаем.
  const [cooldownEndedAt, setCooldownEndedAt] = useState<number | null>(null);

  const { data: status, dataUpdatedAt, isError, refetch } = useQuery({
    queryKey: ['creator', 'verify_status'],
    queryFn: getVerifyStatus,
    refetchInterval: POLL_INTERVAL_MS,
    enabled: !isRateLimited,
  });

  const needsFresh = cooldownEndedAt !== null && dataUpdatedAt < cooldownEndedAt;

  // BUG-1595: дедлайн — событие, а не число, прочитанное при рендере. Таймер
  // снимает cooldown ровно в срок; focus/visibilitychange перечитывают
  // Date.now(), если таймер проспал в фоне. Новый deadline отменяет старый.
  useEffect(() => {
    const retryAt = draft.rateLimitRetryAt;
    if (retryAt === null) return;
    const expireIfDue = () => {
      if (Date.now() < retryAt) return;
      setCooldownEndedAt(retryAt);
      setRateLimit(null);
    };
    const delay = retryAt - Date.now();
    if (delay <= 0) {
      expireIfDue();
      return;
    }
    const timer = setTimeout(expireIfDue, delay);
    document.addEventListener('visibilitychange', expireIfDue);
    window.addEventListener('focus', expireIfDue);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', expireIfDue);
      window.removeEventListener('focus', expireIfDue);
    };
  }, [draft.rateLimitRetryAt, setRateLimit]);

  useEffect(() => {
    if (!needsFresh && status?.status === 'approved') {
      const timer = setTimeout(() => {
        resetDraft();
        navigate('/tasks', { replace: true });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [needsFresh, status?.status, navigate, resetDraft]);

  const backToProfile = () => navigate('/profile');

  // FTR-1452: перепроверка владения профилем по кнопке.
  // Текст ответа приходит с сервера и НЕ дублируется здесь: тот же текст
  // человек уже получил в боте, и две копии одного вердикта разъедутся.
  const [rechecking, setRechecking] = useState(false);
  const [ownershipMessage, setOwnershipMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const handleRecheck = async () => {
    if (rechecking) return;
    setRechecking(true);
    try {
      const res = await recheckOwnership();
      if (res.retry_after_sec != null) {
        // Бюджет попыток исчерпан — это НЕ вердикт о владении. Проверка
        // в TikTok платная, поэтому попытки ограничены.
        const mins = Math.max(1, Math.ceil(res.retry_after_sec / 60));
        setOwnershipMessage(`Слишком много проверок подряд. Попробуйте через ${mins} мин.`);
      } else {
        setOwnershipMessage(res.message ?? null);
        if (res.outcome === 'ok') {
          await queryClient.invalidateQueries({ queryKey: ['creator', 'verify_status'] });
        }
      }
    } catch {
      setOwnershipMessage('Не получилось проверить. Попробуйте ещё раз через пару минут.');
    } finally {
      setRechecking(false);
    }
  };

  // ── Rate-limited (EC-5) ───────────────────────────────────────────────
  // BUG-1595: пока cooldown идёт, кнопки подачи нет — экран снимает лимит сам.
  if (isRateLimited && retryAfterMs !== null) {
    return (
      <Frame title="Заявка" onBack={backToProfile}>
        <WizardBody style={CENTER}>
          <div style={{ width: 64, height: 64, borderRadius: 32, background: 'rgba(45,37,24,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Ic name="clock" size={32} color={P.warn} />
          </div>
          <div style={{ font: T.title3, color: P.ink }}>Слишком много попыток</div>
          <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
            Попробуй снова через {formatRetryAfter(retryAfterMs)} — экран обновится сам.
          </div>
        </WizardBody>
      </Frame>
    );
  }

  // ── Cooldown истёк, ответа ещё нет (BUG-1595) ─────────────────────────
  // Свежий verify_status обязателен: stale cache не даёт ни подачу, ни redirect.
  if (needsFresh) {
    return (
      <Frame title="Заявка" onBack={backToProfile}>
        <WizardBody style={CENTER}>
          {isError ? (
            <>
              <div style={{ font: T.title3, color: P.ink }}>Не удалось проверить статус</div>
              <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
                Связь прервалась. Заявка никуда не делась — попробуйте прочитать статус ещё раз.
              </div>
            </>
          ) : (
            <div style={{ font: T.title3, color: P.ink }}>Сверяем статус заявки…</div>
          )}
        </WizardBody>
        {isError && <WizardCtaBar label="Повторить" onClick={() => { void refetch(); }} />}
      </Frame>
    );
  }

  // ── Approved ──────────────────────────────────────────────────────────
  if (status?.status === 'approved') {
    return (
      <Frame title="Заявка одобрена" onBack={backToProfile}>
        <WizardBody style={CENTER}>
          <div style={{ width: 72, height: 72, borderRadius: 36, background: '#EFF7E9', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 8px rgba(45,138,78,0.10)' }}>
            <Ic name="check" size={36} color={P.success} />
          </div>
          <div style={{ font: T.title2, color: P.ink }}>Заявка одобрена!</div>
          <div style={{ font: T.subhead, color: P.inkSoft }}>Переходим к заданиям…</div>
        </WizardBody>
      </Frame>
    );
  }

  // ── Rejected ──────────────────────────────────────────────────────────
  if (status?.status === 'rejected') {
    return (
      <Frame title="Заявка" onBack={backToProfile}>
        <WizardBody>
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 16 }}>
            <div style={{ width: 64, height: 64, borderRadius: 32, background: 'rgba(209,67,67,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Ic name="cross" size={28} color={P.error} />
            </div>
          </div>
          <div style={{ font: T.title2, color: P.ink, textAlign: 'center' }}>
            Заявка отклонена
          </div>
          {status.rejection_reason && (
            <div style={{ borderRadius: 12, background: P.terraSoft, border: `1px solid ${P.terraLight}`, padding: '14px 16px' }}>
              <div style={{ font: T.subhead, color: P.ink, lineHeight: 1.5 }}>
                {rejectionReasonLabel(status.rejection_reason)}
              </div>
            </div>
          )}
          <div style={{ font: T.footnote, color: P.inkSoft, textAlign: 'center' }}>
            Вы можете подать заявку повторно
          </div>
        </WizardBody>
        <WizardCtaBar
          label="Подать снова"
          onClick={() => { resetDraft(); navigate('/creator-onboarding/url', { replace: true }); }}
        />
      </Frame>
    );
  }

  // ── needs_info — ручная очередь оператора (BUG-1451 B1) ───────────────
  // Это НЕ спиннер: заявка вышла из автоматического конвейера и ждёт человека.
  // Опрос оставлен включённым, чтобы вердикт оператора долетел без перезагрузки.
  if (status?.status === 'needs_info') {
    // FTR-1452: заявка ждёт не оператора, а действия самого человека —
    // подписаться на сообщество либо вписать код в описание профиля. Кнопка
    // перепроверки показывается ТОЛЬКО в этом случае: во всех остальных
    // needs_info нажимать её бессмысленно, решает человек на нашей стороне.
    const isOwnership = (status.rejection_reason ?? '').startsWith('ownership_');
    // TECH-1456: заявка вышла в ручную очередь из-за отвала распознавателя.
    // Человеку причина не называется — только спокойная правда про сроки.
    const isVisionUnavailable = status.rejection_reason === 'vision_unavailable';
    return (
      <Frame title="Заявка" onBack={backToProfile}>
        <WizardBody style={CENTER}>
          <div
            style={{
              width: 64, height: 64, borderRadius: 32, background: 'rgba(45,37,24,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Ic name="clock" size={32} color={P.warn} />
          </div>
          <div style={{ font: T.title3, color: P.ink }}>
            {isOwnership
              ? 'Подтвердите, что страница ваша'
              : isVisionUnavailable
                ? 'Ваш скрин ушёл на проверку'
                : 'Заявка на ручной проверке'}
          </div>
          <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
            {ownershipMessage
              ?? (isOwnership
                ? 'Мы прислали в бот, что нужно сделать. После этого нажмите «Проверить ещё раз».'
                : isVisionUnavailable
                  ? MANUAL_REVIEW_NOTICE
                  : 'Заявку смотрит оператор — обычно до 1–2 рабочих дней. Мы напишем в боте, как только будет результат.')}
          </div>
        </WizardBody>
        {isOwnership ? (
          <WizardCtaBar
            label={rechecking ? 'Проверяем…' : 'Проверить ещё раз'}
            onClick={handleRecheck}
          />
        ) : (
          <WizardCtaBar label="Закрыть" kind="ink" onClick={backToProfile} />
        )}
      </Frame>
    );
  }

  // ── banned — терминальный (BUG-1451 B1) ───────────────────────────────
  // Без кнопки «Подать снова»: повторная подача бессмысленна и сожжёт суточную
  // корзину лимита. Единственное честное действие — выйти.
  if (status?.status === 'banned') {
    return (
      <Frame title="Заявка" onBack={backToProfile}>
        <WizardBody style={CENTER}>
          <div style={{ width: 64, height: 64, borderRadius: 32, background: 'rgba(209,67,67,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Ic name="cross" size={28} color={P.error} />
          </div>
          <div style={{ font: T.title3, color: P.ink }}>Доступ закрыт</div>
          <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
            {status.rejection_reason
              ? rejectionReasonLabel(status.rejection_reason)
              : 'Подать заявку повторно нельзя. Если считаете это ошибкой — напишите в поддержку.'}
          </div>
        </WizardBody>
        <WizardCtaBar label="Закрыть" kind="ink" onClick={backToProfile} />
      </Frame>
    );
  }

  // ── no_application — заявки нет, можно подать заново (BUG-1595) ───────
  if (status?.status === 'no_application') {
    return (
      <Frame title="Заявка" onBack={backToProfile}>
        <WizardBody style={CENTER}>
          <div style={{ font: T.title3, color: P.ink }}>Заявка не найдена</div>
          <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
            Черновик сохранён — можно подать заявку заново.
          </div>
        </WizardBody>
        <WizardCtaBar
          label="Подать заново"
          onClick={() => { resetDraft(); navigate('/creator-onboarding/url', { replace: true }); }}
        />
      </Frame>
    );
  }

  // ── Незнакомый статус (BUG-1451 B1) ───────────────────────────────────
  // Спиннер обещает «1–2 минуты» и потому лжёт для всего, что не в работе.
  // Новый статус в бэкенде не должен превращаться в вечное вращение.
  if (status?.status && !SPINNER_STATUSES.includes(status.status)) {
    return (
      <Frame title="Заявка" onBack={backToProfile}>
        <WizardBody style={CENTER}>
          <div style={{ width: 64, height: 64, borderRadius: 32, background: 'rgba(45,37,24,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Ic name="clock" size={32} color={P.inkSoft} />
          </div>
          <div style={{ font: T.title3, color: P.ink }}>Статус уточняется</div>
          <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
            Мы напишем в боте, как только будет результат.
          </div>
        </WizardBody>
        <WizardCtaBar label="Закрыть" kind="ink" onClick={backToProfile} />
      </Frame>
    );
  }

  // ── Pending (default) ─────────────────────────────────────────────────
  return (
    <Frame title="Заявка на проверке" onBack={backToProfile}>
      <WizardBody style={{ ...CENTER, gap: 14 }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', border: `3px solid ${P.terracotta}`, borderTopColor: 'transparent', animation: 'spin 0.9s linear infinite' }} />
        <div style={{ font: T.title3, color: P.ink }}>Проверяем заявку</div>
        <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
          Обычно занимает 1–2 минуты. Если автоматическая проверка не сработает,
          заявку рассмотрят вручную — это до 1–2 рабочих дней. Мы уведомим вас
          в боте.
        </div>
      </WizardBody>
      <WizardCtaBar label="Закрыть" kind="ink" onClick={backToProfile} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Frame>
  );
}
